// menuImportClient — thin admin-portal client for the AI Menu Image Import feature.
//
// It talks only to first-party `/admin` endpoints with the caller's Cognito ID
// token (`Authorization: Bearer <idToken>`), mirroring the existing admin-portal
// fetch convention (see AdminCategories.js / AdminFulfillmentDashboard.js). It
// never handles any provider (Gemini) key — the key lives server-side only.
//
// Two responsibilities:
//   - extractMenu(file, token, opts): validate the file client-side, POST it
//     base64-encoded to POST /admin/menu/extract, and resolve to an
//     ExtractResult for EVERY non-abort outcome (never throws on HTTP/quota/
//     network errors). Only a superseded AbortError is re-thrown.
//   - bulkCreateProducts(products, token, opts): chunk the list to
//     BULK_UPLOAD_MAX_ITEMS, POST each chunk to POST /admin/products/bulk, and
//     return a FLAT results array that accounts for every submitted item with
//     no silent drops (indices are re-based across chunks so they stay unique).

const API_BASE_URL =
  process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// Client-side upload constraints (mirrors the server-side defense-in-depth
// validation in MenuExtractFunction — Req 1.2–1.5).
export const MENU_EXTRACT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
export const SUPPORTED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

// API Gateway rejects request bodies larger than ~10 MB with HTTP 413 BEFORE
// the request reaches the Lambda. base64 inflates bytes by ~33% and the JSON
// envelope adds a little more, so a single PDF near the 10 MB file cap produces
// an oversized body. To stay safely under the gateway limit we split multi-page
// PDFs into ONE request per page (each page is small) and merge the per-page
// results client-side. This also dodges the per-request extraction timeout,
// since every page gets its own Lambda invocation with the full time budget.

// Pages are extracted SERIALLY (one request at a time). Each request already
// takes several seconds at the provider, which naturally paces us well under
// the free-tier limit (Gemini flash ~15 requests/min); firing pages in parallel
// is what tripped the 429s. A small floor between requests guards against fast
// (cached/tiny) pages bursting. Because each page is its OWN request there is no
// shared ~29s API Gateway ceiling, so a large menu may simply take a while —
// completeness is prioritised over speed.
const PAGE_EXTRACT_MIN_INTERVAL_MS = 4000;

// Per-page transient-failure retries (429 / 5xx / network). COMPLETENESS over
// speed: a multi-page extract is FAIL-CLOSED — if any page ultimately fails,
// the whole extraction returns a retryable error rather than a partial menu.
const PAGE_EXTRACT_MAX_RETRIES = 3;
// Short linear backoff for generic transient errors (5xx / network).
const PAGE_EXTRACT_RETRY_BACKOFF_MS = 1500;
// A 429 means the per-minute quota is spent; a sub-second retry is pointless.
// Wait out the quota window (a little over a minute) before retrying so the
// page can actually succeed rather than burning more quota.
const PAGE_EXTRACT_RATELIMIT_BACKOFF_MS = 35000;

// Matches the backend cap (`BULK_UPLOAD_MAX_ITEMS`, default 20) in
// lambda/admin-product-handler/index.py so no single request is rejected for
// exceeding the batch limit.
export const BULK_UPLOAD_MAX_ITEMS = 20;

// User-facing messages (kept graceful; never leak provider internals).
const UNSUPPORTED_TYPE_MESSAGE =
  'Unsupported file type. Supported types are JPG, PNG, and PDF.';
const TOO_LARGE_MESSAGE =
  'File is too large. The maximum allowed size is 10 MB.';
const QUOTA_MESSAGE =
  'Extraction quota is temporarily unavailable. Please try again later.';
const MANUAL_ENTRY_MESSAGE =
  "We couldn't extract the menu automatically. Please try again or enter the menu manually.";
const MISSING_API_MESSAGE =
  'Missing API base URL (REACT_APP_ADMIN_API_URL).';

/**
 * @typedef {Object} ExtractedItem
 * @property {string} title
 * @property {string} description
 * @property {string} price
 *
 * @typedef {Object} ExtractedCategory
 * @property {string} title
 * @property {ExtractedItem[]} items
 *
 * @typedef {Object} ExtractedMenu
 * @property {ExtractedCategory[]} categories
 *
 * @typedef {Object} ExtractResult
 * @property {ExtractedMenu|null} menu
 * @property {boolean} ok
 * @property {boolean} canRetry
 * @property {boolean} canEnterManually
 * @property {string=} error
 */

/** Build a graceful ExtractResult (never throws). */
function extractFailure(error, { canRetry = true, canEnterManually = true } = {}) {
  return { menu: null, ok: false, canRetry, canEnterManually, error };
}

/** True when the thrown value is an fetch/DOM abort. */
function isAbortError(err) {
  return !!err && (err.name === 'AbortError' || err.code === 20);
}

/**
 * Read a File/Blob and return its raw base64 (no `data:...;base64,` prefix),
 * which is what POST /admin/menu/extract expects for `file_base64`.
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
      reader.onload = () => {
        const result = String(reader.result || '');
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * extractMenu — validate a menu document, upload it base64-encoded, and resolve
 * to an ExtractResult for every non-abort outcome. Never throws on HTTP, quota,
 * or network errors; re-throws only a superseded AbortError.
 *
 * @param {File} file           The selected JPG/PNG/PDF document (<= 10 MB).
 * @param {string} token        Cognito ID token (Auth.currentSession().getIdToken().getJwtToken()).
 * @param {Object} [opts]
 * @param {AbortSignal} [opts.signal]  Optional signal to cancel/supersede the request.
 * @param {(progress: {current:number,total:number,waiting:boolean}) => void} [opts.onProgress]
 *        Optional callback invoked as each page of a multi-page PDF is
 *        processed, so the UI can show "page X of N" progress. Not called for
 *        single-request (image / single-page PDF) extractions.
 * @returns {Promise<ExtractResult>}
 */
export async function extractMenu(file, token, opts = {}) {
  // Client-side type/size validation for fast feedback (Req 1.2–1.5). The
  // backend re-validates as defense in depth.
  if (!file) {
    return extractFailure(UNSUPPORTED_TYPE_MESSAGE, { canRetry: false });
  }
  const mimeType = file.type || '';
  if (!SUPPORTED_MIME_TYPES.includes(mimeType)) {
    return extractFailure(UNSUPPORTED_TYPE_MESSAGE, { canRetry: false });
  }
  if (typeof file.size === 'number' && file.size > MENU_EXTRACT_MAX_BYTES) {
    return extractFailure(TOO_LARGE_MESSAGE, { canRetry: false });
  }

  if (!API_BASE_URL) {
    return extractFailure(MISSING_API_MESSAGE);
  }

  // Multi-page PDFs are split into ONE request per page so no single request
  // exceeds the API Gateway body limit (root cause of the HTTP 413), and each
  // page gets its own Lambda time budget. Single-page PDFs, images, and any
  // split failure fall back to a single request over the original file.
  if (mimeType === 'application/pdf') {
    let pageBlobs = null;
    try {
      pageBlobs = await splitPdfIntoPageBlobs(file);
    } catch (err) {
      pageBlobs = null; // any split failure -> single-request fallback
    }
    if (Array.isArray(pageBlobs) && pageBlobs.length > 1) {
      return extractMenuFromPages(pageBlobs, mimeType, file.name || '', token, opts);
    }
  }

  try {
    const fileBase64 = await fileToBase64(file);
    return await postExtractRequest(
      fileBase64, mimeType, file.name || '', token, opts.signal
    );
  } catch (err) {
    // A superseded/cancelled request is the ONLY case we re-throw so callers
    // can distinguish it from a real failure.
    if (isAbortError(err)) throw err;
    // Network error / read failure -> graceful manual-entry path (Req 9.2).
    return extractFailure(MANUAL_ENTRY_MESSAGE, { canRetry: true, canEnterManually: true });
  }
}

/**
 * postExtractRequest — POST one base64 document to /admin/menu/extract and map
 * the response to an ExtractResult. Never throws except on a superseded
 * AbortError (so callers can distinguish cancellation from a real failure).
 * Shared by the single-request path and each per-page request.
 *
 * @returns {Promise<ExtractResult & {statusCode?: number}>}
 */
async function postExtractRequest(fileBase64, mimeType, filename, token, signal) {
  const response = await fetch(`${API_BASE_URL}/admin/menu/extract`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      file_base64: fileBase64,
      mime_type: mimeType,
      filename: filename || '',
    }),
    signal,
  });

  const data = await response.json().catch(() => null);

  if (response.ok) {
    return {
      menu: (data && data.menu) || null,
      ok: true,
      statusCode: response.status,
      canRetry: data && typeof data.canRetry === 'boolean' ? data.canRetry : true,
      canEnterManually:
        data && typeof data.canEnterManually === 'boolean' ? data.canEnterManually : true,
      // Phase 2 (Req 10.1, 10.4): pass embedded image URLs and flat-scan flag
      // through to the caller (MenuImportPage) so they can be forwarded to
      // PreviewEditScreen for per-item association. Absent in Phase 1 responses
      // (backend omits these fields) — default to empty list / false.
      images: Array.isArray(data && data.images) ? data.images : [],
      flatScan: (data && data.flatScan) === true,
      imageNote: (data && typeof data.imageNote === 'string') ? data.imageNote : '',
      imageWarnings: Array.isArray(data && data.imageWarnings) ? data.imageWarnings : [],
    };
  }

  // Quota exhausted (Req 9.1): try-again-later, no paid/auto-billed fallback.
  if (response.status === 429) {
    return {
      ...extractFailure(QUOTA_MESSAGE, { canRetry: true, canEnterManually: true }),
      statusCode: 429,
    };
  }

  // Any other failure (4xx/5xx/503) degrades gracefully toward manual entry
  // (Req 9.2). Prefer a backend-provided message when present.
  const backendError = data && typeof data === 'object' ? data.error : '';
  return {
    ...extractFailure(String(backendError || '').trim() || MANUAL_ENTRY_MESSAGE, {
      canRetry: true,
      canEnterManually: true,
    }),
    statusCode: response.status,
  };
}

/**
 * splitPdfIntoPageBlobs — split a PDF File into one single-page PDF Blob per
 * page using pdf-lib (pure JS, runs in the browser). Returns an array of Blobs
 * in page order, or `null` when splitting is not applicable/possible (not a
 * PDF, a single page, an encrypted/corrupt document, or pdf-lib unavailable) so
 * the caller transparently falls back to a single request. Never throws.
 *
 * @param {File|Blob} file
 * @returns {Promise<Blob[]|null>}
 */
export async function splitPdfIntoPageBlobs(file) {
  try {
    const { PDFDocument } = await import('pdf-lib');
    const bytes = await file.arrayBuffer();
    const src = await PDFDocument.load(bytes, { ignoreEncryption: false });
    const pageCount = src.getPageCount();
    if (!pageCount || pageCount <= 1) return null;

    const blobs = [];
    for (let i = 0; i < pageCount; i += 1) {
      const out = await PDFDocument.create();
      const [page] = await out.copyPages(src, [i]);
      out.addPage(page);
      const pageBytes = await out.save();
      blobs.push(new Blob([pageBytes], { type: 'application/pdf' }));
    }
    return blobs;
  } catch (err) {
    return null;
  }
}

/** Normalize a category title for same-category merging across pages. */
function normalizeCategoryTitle(title) {
  return String(title == null ? '' : title).replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Normalize an item title for intra-category de-duplication across pages. */
function normalizeItemTitle(title) {
  return String(title == null ? '' : title).replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * mergeExtractedMenus — concatenate per-page menus into one, preserving page
 * order. Categories with the same normalized title (e.g. a section spanning a
 * page break) are combined into the first occurrence.
 *
 * De-duplicates items WITHIN each merged category by normalized title, keeping
 * the first occurrence. This prevents the same product being listed twice when
 * a menu section repeats across page boundaries (the multi-page split extracts
 * each page independently, so an overlapping section yields the same items on
 * two pages). Items with a blank title are always kept (nothing to match on).
 * A section that merely CONTINUES onto the next page (different items under the
 * same header) is unaffected — only exact-title repeats are dropped. Pure.
 *
 * @param {ExtractedMenu[]} menus
 * @returns {ExtractedMenu}
 */
export function mergeExtractedMenus(menus) {
  const categories = [];
  const indexByTitle = new Map();
  // Parallel to `categories`: the set of normalized item titles already present
  // in each merged category, used to drop duplicate products.
  const seenItemTitles = [];

  const appendItems = (catIndex, items) => {
    const target = categories[catIndex];
    const seen = seenItemTitles[catIndex];
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const itemKey = normalizeItemTitle(item.title);
      // A titled item that already exists in this category is a duplicate; skip
      // it. Blank-title items can't be matched, so they're always kept.
      if (itemKey && seen.has(itemKey)) continue;
      if (itemKey) seen.add(itemKey);
      target.items.push(item);
    }
  };

  for (const menu of Array.isArray(menus) ? menus : []) {
    const cats = menu && Array.isArray(menu.categories) ? menu.categories : [];
    for (const cat of cats) {
      if (!cat || typeof cat !== 'object') continue;
      const items = Array.isArray(cat.items) ? cat.items : [];
      const key = normalizeCategoryTitle(cat.title);
      if (key && indexByTitle.has(key)) {
        appendItems(indexByTitle.get(key), items);
      } else {
        const catIndex = categories.length;
        categories.push({ ...cat, items: [] });
        seenItemTitles.push(new Set());
        if (key) indexByTitle.set(key, catIndex);
        appendItems(catIndex, items);
      }
    }
  }
  return { categories };
}

/** Sleep helper for per-page retry backoff. */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * extractOnePageWithRetry — extract a single page Blob, retrying transient
 * failures (429 / 5xx / network) with backoff. Re-throws only AbortError.
 *
 * @returns {Promise<ExtractResult & {statusCode?: number}>}
 */
async function extractOnePageWithRetry(blob, mimeType, filename, token, signal, onWait) {
  let attempt = 0;
  let last = null;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const fileBase64 = await fileToBase64(blob);
      last = await postExtractRequest(fileBase64, mimeType, filename, token, signal);
    } catch (err) {
      if (isAbortError(err)) throw err;
      last = extractFailure(MANUAL_ENTRY_MESSAGE, { canRetry: true, canEnterManually: true });
    }
    if (last && last.ok) return last;

    attempt += 1;
    if (attempt > PAGE_EXTRACT_MAX_RETRIES) return last;
    // A 429 needs a long, quota-window-sized wait; other transient errors get a
    // short linear backoff. Signal the long rate-limit wait so the UI can
    // explain the pause.
    const isRateLimit = last && last.statusCode === 429;
    if (isRateLimit && typeof onWait === 'function') onWait();
    const backoff = isRateLimit
      ? PAGE_EXTRACT_RATELIMIT_BACKOFF_MS
      : PAGE_EXTRACT_RETRY_BACKOFF_MS * attempt;
    await delay(backoff);
  }
}

/**
 * extractMenuFromPages — extract every page Blob (bounded concurrency), then
 * merge FAIL-CLOSED. If any page ultimately fails, the whole extraction returns
 * a retryable error (preferring the quota message when a page was rate-limited)
 * rather than a silently-incomplete menu. On full success, categories are
 * merged in page order and per-page images/flat-scan flags are combined.
 *
 * @returns {Promise<ExtractResult>}
 */
async function extractMenuFromPages(pageBlobs, mimeType, filename, token, opts = {}) {
  const results = [];
  const total = pageBlobs.length;
  const onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : () => {};

  // Process pages ONE AT A TIME to stay under the free-tier rate limit. Fail
  // FAST: the moment a page can't be extracted (after its retries) we stop and
  // report a retryable error — there's no point spending more quota on the
  // remaining pages when the result would be incomplete anyway (fail-closed).
  for (let index = 0; index < total; index += 1) {
    if (index > 0) {
      await delay(PAGE_EXTRACT_MIN_INTERVAL_MS);
    }
    onProgress({ current: index + 1, total, waiting: false });
    const result = await extractOnePageWithRetry(
      pageBlobs[index], mimeType, `${filename || 'menu'} (page ${index + 1})`,
      token, opts.signal,
      () => onProgress({ current: index + 1, total, waiting: true })
    );
    if (!result || !result.ok) {
      const chosen = result || {};
      return extractFailure(chosen.error || MANUAL_ENTRY_MESSAGE, {
        canRetry: chosen.canRetry !== false,
        canEnterManually: chosen.canEnterManually !== false,
      });
    }
    results.push(result);
  }

  const merged = mergeExtractedMenus(results.map((r) => r.menu || { categories: [] }));
  const images = results.reduce(
    (acc, r) => acc.concat(Array.isArray(r.images) ? r.images : []), []
  );
  return {
    menu: merged,
    ok: true,
    canRetry: true,
    canEnterManually: true,
    images,
    // Only report a flat scan when EVERY page was a flat scan (otherwise some
    // pages carried real embedded images).
    flatScan: results.length > 0 && results.every((r) => r.flatScan === true),
    imageNote: '',
    imageWarnings: results.reduce(
      (acc, r) => acc.concat(Array.isArray(r.imageWarnings) ? r.imageWarnings : []), []
    ),
  };
}

/** Split a list into contiguous chunks of at most `size`. */
function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) {
    out.push(list.slice(i, i + size));
  }
  return out;
}

/**
 * bulkCreateProducts — create reviewed products via the existing
 * POST /admin/products/bulk route. The list is chunked to
 * BULK_UPLOAD_MAX_ITEMS and each chunk is posted independently; results are
 * concatenated into a single FLAT array in which every submitted item is
 * accounted for exactly once (created-or-failed) with a stable, unique index.
 *
 * Re-throws only a superseded AbortError. Any other failure (HTTP or network)
 * for a chunk is recorded as a per-item failure rather than dropping items.
 *
 * @param {Object[]} products   Bulk-create product bodies.
 * @param {string} token        Cognito ID token.
 * @param {Object} [opts]
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<Array<{index:number, ok:boolean, productId?:string, priceId?:string, imageUrl?:string, error?:string, statusCode?:number}>>}
 */
export async function bulkCreateProducts(products, token, opts = {}) {
  const list = Array.isArray(products) ? products : [];
  const results = [];

  if (list.length === 0) return results;

  const chunks = chunk(list, BULK_UPLOAD_MAX_ITEMS);
  let baseIndex = 0;

  for (const group of chunks) {
    const chunkStart = baseIndex;
    baseIndex += group.length;

    // Record a uniform per-item failure for the entire chunk (no silent drops).
    const failChunk = (error, statusCode) => {
      for (let i = 0; i < group.length; i += 1) {
        results.push({
          index: chunkStart + i,
          ok: false,
          error,
          statusCode,
        });
      }
    };

    if (!API_BASE_URL) {
      failChunk(MISSING_API_MESSAGE, 0);
      continue;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ products: group }),
        signal: opts.signal,
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const backendError = data && typeof data === 'object' ? data.error : '';
        failChunk(
          String(backendError || '').trim() || 'Failed to create products',
          response.status,
        );
        continue;
      }

      const chunkResults = data && Array.isArray(data.results) ? data.results : null;

      // Defensive: a malformed/missing results array must not silently drop the
      // chunk's items — account for each as a failure instead.
      if (!chunkResults) {
        failChunk('Malformed bulk-create response', response.status);
        continue;
      }

      // Re-base each per-item result index onto the global submission order and
      // preserve every per-item field.
      const seen = new Set();
      for (const r of chunkResults) {
        const localIndex =
          r && typeof r.index === 'number' ? r.index : chunkResults.indexOf(r);
        seen.add(localIndex);
        results.push({ ...r, index: chunkStart + localIndex });
      }

      // If the backend omitted any item from its results, still account for it.
      for (let i = 0; i < group.length; i += 1) {
        if (!seen.has(i)) {
          results.push({
            index: chunkStart + i,
            ok: false,
            error: 'Item not reported by server',
            statusCode: response.status,
          });
        }
      }
    } catch (err) {
      if (isAbortError(err)) throw err;
      failChunk('Network error creating products', 0);
    }
  }

  // Keep results in stable submission order.
  results.sort((a, b) => a.index - b.index);
  return results;
}

// ---------------------------------------------------------------------------
// URL-based menu extraction — POST /admin/menu/extract-url
// Completely separate from extractMenu (file-based). Never modifies that path.
// ---------------------------------------------------------------------------

/**
 * extractMenuFromUrl — submit a restaurant website URL for menu extraction.
 *
 * The backend renders the page with headless Chrome (Browserless.io), strips
 * it to clean text, sends it to Gemini, downloads product images, and returns
 * the same ExtractResult shape as extractMenu so PreviewEditScreen works
 * unchanged.
 *
 * @param {string} url     The restaurant menu page URL.
 * @param {string} token   Cognito ID token.
 * @param {Object} [opts]
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<ExtractResult>}
 */
export async function extractMenuFromUrl(url, token, opts = {}) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (!trimmed) {
    return extractFailure('Please enter a URL.', { canRetry: false, canEnterManually: true });
  }
  if (!/^https?:\/\/.+/i.test(trimmed)) {
    return extractFailure(
      'Please enter a valid http:// or https:// URL.',
      { canRetry: false, canEnterManually: true }
    );
  }
  if (!API_BASE_URL) {
    return extractFailure(MISSING_API_MESSAGE);
  }

  try {
    const response = await fetch(`${API_BASE_URL}/admin/menu/extract-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ url: trimmed }),
      signal: opts.signal,
    });

    const data = await response.json().catch(() => null);

    if (response.ok) {
      return {
        menu: (data && data.menu) || null,
        ok: true,
        canRetry: data && typeof data.canRetry === 'boolean' ? data.canRetry : true,
        canEnterManually:
          data && typeof data.canEnterManually === 'boolean' ? data.canEnterManually : true,
        images: Array.isArray(data && data.images) ? data.images : [],
        flatScan: (data && data.flatScan) === true,
        imageNote: (data && typeof data.imageNote === 'string') ? data.imageNote : '',
        imageWarnings: Array.isArray(data && data.imageWarnings) ? data.imageWarnings : [],
      };
    }

    if (response.status === 429) {
      return extractFailure(QUOTA_MESSAGE, { canRetry: true, canEnterManually: true });
    }

    const backendError = data && typeof data === 'object' ? data.error : '';
    return extractFailure(
      String(backendError || '').trim() || MANUAL_ENTRY_MESSAGE,
      { canRetry: true, canEnterManually: true }
    );
  } catch (err) {
    if (isAbortError(err)) throw err;
    return extractFailure(MANUAL_ENTRY_MESSAGE, { canRetry: true, canEnterManually: true });
  }
}
